import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useState} from 'react'
import {UserQualificationFormModal} from '@/features/users/components/user-qualification-form-modal/UserQualificationFormModal'
import {UserQualificationsList} from '@/features/users/components/user-qualifications-list/UserQualificationsList'
import {UserProfessionalRegisterForm} from '@/features/users/components/user-professional-register-form/UserProfessionalRegisterForm'
import {UserEcmScoreForm} from '@/features/users/components/user-ecm-score-form/UserEcmScoreForm'
import {UserCurriculumForm} from '@/features/users/components/user-curriculum-form/UserCurriculumForm'
import {UserProfessionalInsuranceForm} from '@/features/users/components/user-professional-insurance-form/UserProfessionalInsuranceForm'
import {UserAbsenceIncopatibilityForm} from '@/features/users/components/user-absence-incopatibility-form/UserAbsenceIncopatibilityForm'
import {FormGridTitle} from '@/components/ui/form-atoms/FormAtoms'
import {UserEducationFormValidationSchema} from '@/features/users/types'
import {InputSelectMonthYear} from '@/components/commons/input-select-month-year/InputSelectMonthYear'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {StyledGridItem} from '../user-professional-insurance-form/style'

export const UserEducationForm = () => {
    const {t} = useTranslation()
    const [isQualificationFormModalOpen, setIsQualificationFormModalOpen] = useState(false)
    const [selectedQualification, setSelectedQualification] = useState<number | null>(null)

    // methods
    const form = useFormContext<UserEducationFormValidationSchema>()

    // form observer
    const qualifications = useWatch({control: form.control, name: 'qualifications'})

    return (
        <Flexbox direction="column" gap={8} fullWidth>
            <Flexbox direction="column" gap={4.5} fullWidth>
                <Flexbox justify="space-between" align="center" fullWidth>
                    <FormGridTitle>{t('commons:education')}</FormGridTitle>
                    {qualifications && qualifications?.length > 0 && (
                        <Button size="sm" variant="secondary" onClick={() => setIsQualificationFormModalOpen(true)}>
                            <PlusIcon />
                            {t('commons:add')}
                        </Button>
                    )}
                </Flexbox>

                <UserQualificationsList
                    setSelectedQualification={setSelectedQualification}
                    setIsQualificationFormModalOpen={setIsQualificationFormModalOpen}
                />
            </Flexbox>

            {(isQualificationFormModalOpen || selectedQualification != null) && (
                <UserQualificationFormModal
                    selectedQualification={selectedQualification}
                    title={selectedQualification ? t('users:edit_qualification') : t('users:add_qualification')}
                    onClose={() => {
                        setSelectedQualification(null)
                        setIsQualificationFormModalOpen(false)
                    }}
                />
            )}

            <Flexbox fullWidth direction="column" gap={2.5}>
                <FormGridTitle>{t('users:professional_register')}</FormGridTitle>
                <UserProfessionalRegisterForm />
            </Flexbox>

            <Flexbox fullWidth direction="column" gap={2.5}>
                <FormGridTitle>{t('users:ecm')}</FormGridTitle>
                <UserEcmScoreForm />
            </Flexbox>

            <Flexbox fullWidth direction="column" gap={2.5}>
                <FormGridTitle>{t('users:cv')}</FormGridTitle>
                <UserCurriculumForm />
            </Flexbox>

            <Flexbox fullWidth direction="column" gap={2.5}>
                <FormGridTitle>{t('users:professional_insurance')}</FormGridTitle>
                <UserProfessionalInsuranceForm />
            </Flexbox>

            <Flexbox fullWidth direction="column" gap={2.5}>
                <FormGridTitle>{t('users:absence_incompatibility')}</FormGridTitle>
                <UserAbsenceIncopatibilityForm />
            </Flexbox>

            <Flexbox direction="column" fullWidth gap={2.5}>
                <FormGridTitle>{t('users:work_suitable_up_to_title')}</FormGridTitle>
                <Grid columns={6} fullWidth gap={4}>
                    <StyledGridItem>
                        <Controller
                            control={form.control}
                            name="workSuitableUpTo"
                            render={({field}) => (
                                <InputSelectMonthYear
                                    label={t('users:eligible_until')}
                                    onChange={field.onChange}
                                    value={field.value ?? null}
                                    errorMessage={form.formState.errors.workSuitableUpTo?.message}
                                />
                            )}
                        />
                    </StyledGridItem>
                </Grid>
            </Flexbox>
        </Flexbox>
    )
}
